import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserInfo, passwordInfo, AliasLoginInfo, AccountSearchListModel, GetDebtorSummary, GetDebtorHistory } from '../models/Profile-view.model';
import { UsersViewModel, CompanyInfoViewModel, UserInfoViewModel, ActiveUserViewModel, PaymentModel, MfaModel, UpdatedCompanyInfoViewModel } from '../models/account-ledger.model';
import { ApiResponse } from '../models/api-response.model';
// tslint:disable-next-line:import-blacklist
import { BehaviorSubject } from 'rxjs';
import { BrandingConfigViewModel } from '../models/branding-config-view-modl';
import { PaymentApiResponse } from '../models/check-out.model';
import { AuthService } from './auth.service';
import { B2brestApitokens, B2BV2APIClientCredentials } from '../models/b2brestApitokens';
import { AccountDeliveryAddress } from '../models/account-delivery-address';

@Injectable()
export class MyAccountService {

  private baseUrl = 'api/MyAccount';
  userInfo: ActiveUserViewModel = {} as ActiveUserViewModel;
  address: AccountDeliveryAddress = {} as AccountDeliveryAddress;

  private userInfoSource = new BehaviorSubject<ActiveUserViewModel>(this.userInfo);
  currentUserInfo = this.userInfoSource.asObservable();

  private addressSource = new BehaviorSubject<AccountDeliveryAddress>(this.address);
  currentAddress = this.addressSource.asObservable();

  public companyProfile$ = new BehaviorSubject<CompanyInfoViewModel>(null);

  constructor(
    private http: HttpClient,
    private authService: AuthService

  ) { }

  changeUserInfo(user: ActiveUserViewModel): void {
    this.userInfoSource.next(user);
  }

  monitorAddressChange(address: AccountDeliveryAddress): void {
    this.addressSource.next(address);
  }

  // get company profile from the serve
  getCompanyProfile(): Observable<CompanyInfoViewModel> {
    return this.http.get<CompanyInfoViewModel>(`${this.baseUrl}/GetCompanyProfile`)
      .pipe(catchError(this.authService.handleError));
  }

  getDebtOrDetails(): Observable<any> {
    return this.http.get(`${this.baseUrl}/GetDebtOrDetails`)
      .pipe(catchError(this.authService.handleError));
  }

  updateDebtorDetails(companyInfo: UpdatedCompanyInfoViewModel): Observable<any> {
    return this.http.post(this.baseUrl + '/UpdateDebtorDetails', companyInfo)
      .pipe(catchError(this.authService.handleError));
  }

  GetUsers(): Observable<UsersViewModel> {
    return this.http.get<UsersViewModel>(`${this.baseUrl}/GetUsers`)
      .pipe(catchError(this.authService.handleError));
  }

  // post company profile to the backend
  updateCompanyProfile(companyInfo: CompanyInfoViewModel): Observable<any> {
    return this.http.post(`${this.baseUrl}/UpdateCompanyProfile`, companyInfo)
      .pipe(catchError(this.authService.handleError));
  }

  // post feeback info. to the backend
  passFeedbackInfo(feedbackInfo: string, imageFile: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/SendFeedback?feedback=` + feedbackInfo, imageFile)
      .pipe(catchError(this.authService.handleError));
  }

  // post personal info. to the backend and update in the DB
  updatePersonalInfo(personalInfo: UserInfo): Observable<UserInfo> {
    return this.http.post<UserInfo>(`${this.baseUrl}/UpdateUserProfile`, personalInfo)
      .pipe(catchError(this.authService.handleError));
  }

  // post password info. to the backend
  UpdateUserPassword(passwordArr: passwordInfo): Observable<any> {
    return this.http.post(`${this.baseUrl}/UpdateUserPassword`, passwordArr).pipe(catchError(this.authService.handleError));
  }

  GetAccountUserList(accountId: any): Observable<any> {
    return this.http.get(`${this.baseUrl}/GetUserList?accountId=` + accountId).pipe(catchError(this.authService.handleError));
  }

  AliasLogin(aliasLoginInfo: AliasLoginInfo): Observable<any> {
    return this.http.post(`Account/Impersonate`, aliasLoginInfo).pipe(catchError(this.authService.handleError));
  }

  BackToMyLogin(): Observable<any> {
    return this.http.post(`Account/BackToMyLogin`, null).pipe(catchError(this.authService.handleError));
  }

  getaccountSearchList(accountNameOrId: string): Observable<AccountSearchListModel[]> {
    return this.http.get<AccountSearchListModel[]>(`${this.baseUrl}/GetAccountByNameOrId?accountNameOrId=` + encodeURIComponent(accountNameOrId)).pipe(catchError(this.authService.handleError));
  }

  CreateNewUser(model: UserInfoViewModel): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.baseUrl}/CreateNewUser`, model)
      .pipe(catchError(this.authService.handleError));
  }

  DeleteUser(loginId: string): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this.baseUrl}/DeleteUser?loginId=${loginId}`)
      .pipe(catchError(this.authService.handleError));
  }

  UpdateUserProfile(model: UserInfoViewModel): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.baseUrl}/UpdateUserProfile`, model)
      .pipe(catchError(this.authService.handleError));
  }

  UpdateUserMFAOption(model: MfaModel): Observable<any> {
    return this.http.post(`${this.baseUrl}/UpdateUserMFAOption`, model)
    .pipe(catchError(this.authService.handleError));
  }

  GetSalesDocuments(loginId?: string): Observable<UserInfoViewModel> {
    return this.http.get<UserInfoViewModel>(this.baseUrl + '/GetSalesDocuments?loginId=' + loginId)
    .pipe(catchError(this.authService.handleError));
  }

  UpdateSalesDocuments(model: UserInfoViewModel): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.baseUrl + '/UpdateSalesDocuments', model)
    .pipe(catchError(this.authService.handleError));
  }

  getDebtorSummary(accountId: string): Observable<GetDebtorSummary> {
    return this.http.get<GetDebtorSummary>(`${this.baseUrl}/GetDebtorSummary?accountId=` + accountId)
      .pipe(catchError(this.authService.handleError));
  }

  getDebtorHistory(accountId: string): Observable<Array<GetDebtorHistory>> {
    return this.http.get<Array<GetDebtorHistory>>(`${this.baseUrl}/GetDebtorHistory?accountId=` + accountId)
      .pipe(catchError(this.authService.handleError));
  }

  getBrandingConfigView(): Observable<BrandingConfigViewModel> {
    return this.http.get<BrandingConfigViewModel>(`${this.baseUrl}/GetBrandingConfigView`)
    .pipe(catchError(this.authService.handleError));
  }

  GetCurrentPublishableKey(): Observable<string> {
    return this.http.get<string>(`${this.baseUrl}/GetCurrentPublishableKey`)
    .pipe(catchError(this.authService.handleError));
  }

  SaveBrandingConfig(url: string, accentColour: string, imageFile: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.baseUrl}/SaveBrandingConfig?url=${url}&accentColour=${encodeURIComponent(accentColour)}`, imageFile)
    .pipe(catchError(this.authService.handleError));
  }

  Payment(paymentFor: string, orderNumber: string, orderSuffix: string, processed: string, postPayStatus: string,
    reference: string, responseCode: string): Observable<PaymentModel> {
    return this.http.get<PaymentModel>(this.baseUrl + '/Payment?paymentFor=' + paymentFor
      + '&orderNumber=' + orderNumber
      + '&orderSuffix=' + orderSuffix
      + '&processed=' + processed
      + '&postPayStatus=' + postPayStatus
      + '&reference=' + reference
      + '&responseCode=' + responseCode)
      .pipe(catchError(this.authService.handleError));
  }

  ContinueToPayment(paymentType: string, orderNumber: string, orderSuffix: string, referenceNo: string, orderTotal: string,
    customer_number: string, card_scheme: string, name_on_card: string, notes: string): Observable<PaymentApiResponse> {
    return this.http.get<PaymentApiResponse>(this.baseUrl + '/ContinueToPayment?paymentType=' + paymentType
    + '&orderNumber=' + orderNumber
    + '&orderSuffix=' + orderSuffix
    + '&referenceNo=' + referenceNo
    + '&orderTotal=' + orderTotal
    + '&customer_number=' + customer_number
    + '&card_scheme=' + card_scheme
    + '&name_on_card=' + name_on_card
    + '&notes=' + notes)
    .pipe(catchError(this.authService.handleError));
  }

  getCurrentToken(): Observable<B2brestApitokens> {
    return this.http.get<B2brestApitokens>(`${this.baseUrl}/GetCurrentToken`)
    .pipe(catchError(this.authService.handleError));
  }

  generateToken(email: string): Observable<B2brestApitokens> {
    return this.http.get<B2brestApitokens>(`${this.baseUrl}/GenerateNewB2BRestAPIToken?email=${email}`)
    .pipe(catchError(this.authService.handleError));
  }

  searchPostCode(input: any): Observable<any> {
    return this.http.get(`${this.baseUrl}/SearchPostcode?postcode=${input}`)
    .pipe(catchError(this.authService.handleError));
  }

  GetAPIIntegrationStatus(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this.baseUrl}/GetAPIIntegrationStatus`)
    .pipe(catchError(this.authService.handleError));
  }

  createNewPaymentIdWindcave(): string {
    return `${window.location.origin}/${this.baseUrl}/Create_New_Payment_Id_Windcave`
  }

  GetB2BAPIV2Credentials(accountId: string, isSandbox: boolean): Observable<B2BV2APIClientCredentials[]> {
    return this.http.get<B2BV2APIClientCredentials[]>(this.baseUrl + '/GetB2BV2Credentials?accountId='+ accountId + '&isSandbox=' + isSandbox).pipe(catchError(this.authService.handleError));
  }

  GenerateB2BAPIV2Credentials(displayName: string, isSandbox: boolean): Observable<B2BV2APIClientCredentials> {
    return this.http.get<B2BV2APIClientCredentials>(this.baseUrl + '/GenerateB2BAPIV2Credentials?displayName=' + displayName + '&isSandbox=' + isSandbox).pipe(catchError(this.authService.handleError));
  }

  DeleteB2BAPIV2Credentials(id: string, isSandbox: boolean): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + '/DeleteB2BAPIV2Credentials?id=' + id + '&isSandbox=' + isSandbox).pipe(catchError(this.authService.handleError));
  }
}
